// Web Push client helpers. Every function is defensive: a missing key,
// denied permission, unsupported browser, or network error returns a falsy
// result and never throws, so the UI can degrade quietly.
import { supabase } from "../lib/supabase";

// ─── Why this is written the long way ────────────────────────────────────────
// Reading a REACT_APP_* variable that is NOT defined at build time makes CRA
// give up on substituting the member access and emit the ENTIRE env table into
// the bundle instead, then read the property off it at runtime. Every other
// REACT_APP_* value rides along — that is how the Finnhub key reached main.js
// even after nothing in the app referenced it any more.
//
// Destructuring names the one variable this module wants, so webpack replaces
// exactly that and nothing else. A missing value still degrades quietly, which
// is this file's whole contract.
const { REACT_APP_VAPID_PUBLIC_KEY: VAPID_PUBLIC = "" } = process.env;

export function pushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

// iOS only allows web push when the app is installed to the home screen
// (standalone display mode), iOS 16.4+.
export function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)")?.matches
    || window.navigator.standalone === true;
}

export function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js");
  } catch (e) {
    console.warn("[push] SW register failed:", e.message);
    return null;
  }
}

// Returns "granted" | "denied" | "unavailable"
export async function enablePush() {
  if (!pushSupported() || !VAPID_PUBLIC) return "unavailable";
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return permission; // "denied" / "default"

    const reg = await registerServiceWorker();
    if (!reg) return "unavailable";
    await navigator.serviceWorker.ready;

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC),
      });
    }

    await saveSubscription(sub);
    return "granted";
  } catch (e) {
    console.warn("[push] enable failed:", e.message);
    return "unavailable";
  }
}

async function saveSubscription(sub) {
  if (!supabase || !sub) return;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) return;
    const json = sub.toJSON();
    await supabase.from("push_subscriptions").upsert({
      user_id:    session.user.id,
      endpoint:   sub.endpoint,
      p256dh:     json.keys?.p256dh || "",
      auth:       json.keys?.auth || "",
      updated_at: new Date().toISOString(),
    }, { onConflict: "endpoint" });
  } catch (e) {
    console.warn("[push] save subscription failed:", e.message);
  }
}

export async function disablePush() {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    const sub = await reg?.pushManager?.getSubscription();
    if (sub) {
      if (supabase) {
        try { await supabase.from("push_subscriptions").delete().eq("endpoint", sub.endpoint); } catch { /* ignore */ }
      }
      await sub.unsubscribe();
    }
    return true;
  } catch (e) {
    console.warn("[push] disable failed:", e.message);
    return false;
  }
}

export function currentPermission() {
  if (!("Notification" in window)) return "unavailable";
  return Notification.permission; // "granted" | "denied" | "default"
}

// Fire a single test push to this device's own subscription, so the user can
// confirm notifications actually arrive. Returns { ok, reason }.
export async function sendTestPush() {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  if (currentPermission() !== "granted") return { ok: false, reason: "not-granted" };
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    const sub = await reg?.pushManager?.getSubscription();
    if (!sub) return { ok: false, reason: "no-subscription" };
    const res = await fetch("/.netlify/functions/test-push", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ subscription: sub.toJSON() }),
    });
    const data = await res.json().catch(() => ({}));
    return data.ok ? { ok: true } : { ok: false, reason: data.error || "send-failed" };
  } catch (e) {
    return { ok: false, reason: e.message };
  }
}
